import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  MapPin, 
  Bed, 
  Bath, 
  Users, 
  DollarSign, 
  Clock,
  Upload,
  X,
  Plus
} from "lucide-react";
import SaudiRiyal from "@/components/shared/saudi-riyal";
import { Property as MockProperty, PropertyMedia } from "@/mockData/rooms";
import { Property as ApiProperty } from "@/services/propertiesService";
import { PropertyType as ApiPropertyType, propertyTypesService } from "@/services/propertyTypesService";
import { MediaUpload } from "./media-upload";
import { MapLocationPicker } from "./map-location-picker";

interface PropertyFormProps {
  isOpen: boolean;
  onClose: () => void;
  property?: ApiProperty | null;
  propertyTypes: ApiPropertyType[];
  onSave: (property: any, mediaFiles?: File[]) => void;
}

export function PropertyForm({ isOpen, onClose, property, propertyTypes, onSave }: PropertyFormProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description_en: "",
    description_ar: "",
    type_id: "",
    location: "",
    address: "",
    latitude: "",
    longitude: "",
    full_price: "",
    deposit_type: "fixed" as "fixed" | "percentage",
    deposit_value: "",
    capacity: "",
    bedrooms: "",
    bathrooms: "",
    amenities: [] as string[],
    check_in_time: "15:00:00",
    check_out_time: "12:00:00",
    featured: false,
    active: true,
  });
  const [media, setMedia] = useState<PropertyMedia[]>([]);
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);

  const availableAmenities = [
    { key: 'wifi', name: t('wifi') },
    { key: 'parking', name: t('parking') },
    { key: 'kitchen', name: t('kitchen') },
    { key: 'airConditioning', name: t('airConditioning') },
    { key: 'swimmingPool', name: t('swimmingPool') },
    { key: 'gym', name: t('gym') },
    { key: 'spa', name: t('spa') },
    { key: 'restaurant', name: t('restaurant') },
    { key: 'roomService', name: t('roomService') },
    { key: 'laundry', name: t('laundry') },
    { key: 'balcony', name: t('balcony') },
    { key: 'seaView', name: t('seaView') },
    { key: 'cityView', name: t('cityView') },
  ];

  useEffect(() => {
    if (isOpen) {
      if (property) {
        setFormData({
          name: property.name,
          description_en: property.description_en || property.description || "",
          description_ar: property.description_ar || "",
          type_id: property.type_id.toString(),
          location: property.location,
          address: property.address,
          latitude: property.latitude ? property.latitude.toString() : "",
          longitude: property.longitude ? property.longitude.toString() : "",
          full_price: property.full_price.toString(),
          deposit_type: property.deposit_type,
          deposit_value: property.deposit_value.toString(),
          capacity: property.capacity.toString(),
          bedrooms: property.bedrooms.toString(),
          bathrooms: property.bathrooms.toString(),
          amenities: Array.isArray(property.amenities) ? property.amenities : JSON.parse(property.amenities || "[]"),
          check_in_time: property.check_in_time && property.check_in_time.length === 5 ? property.check_in_time + ":00" : property.check_in_time || "15:00:00",
          check_out_time: property.check_out_time && property.check_out_time.length === 5 ? property.check_out_time + ":00" : property.check_out_time || "12:00:00",
          featured: property.featured || false,
          active: property.is_active !== undefined ? property.is_active : (property.active || true),
        });
        // Convert API PropertyImage to PropertyMedia format
        const convertedMedia: PropertyMedia[] = (property.Media || []).map(img => ({
          id: img.id.toString(),
          property_id: img.property_id.toString(),
          url: img.url,
          type: img.type,
          alt_text: img.alt_text || '',
          title: img.title || '',
          description: img.description || '',
          is_primary: img.is_primary || false,
          created_at: img.created_at || new Date().toISOString()
        }));
        setMedia(convertedMedia);
      } else {
        resetForm();
      }
    }
  }, [isOpen, property]);


  const resetForm = () => {
    setFormData({
      name: "",
      description_en: "",
      description_ar: "",
      type_id: "",
      location: "",
      address: "",
      latitude: "",
      longitude: "",
      full_price: "",
      deposit_type: "fixed",
      deposit_value: "",
      capacity: "",
      bedrooms: "",
      bathrooms: "",
      amenities: [],
      check_in_time: "15:00:00",
      check_out_time: "12:00:00",
      featured: false,
      active: true,
    });
    setMedia([]);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAmenityToggle = (amenity: string) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const handleLocationChange = (location: { latitude: number; longitude: number; address: string }) => {
    setFormData(prev => ({
      ...prev,
      latitude: location.latitude.toString(),
      longitude: location.longitude.toString(),
      address: location.address,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const propertyData = {
        name: formData.name,
        description_en: formData.description_en,
        description_ar: formData.description_ar,
        type_id: parseInt(formData.type_id),
        location: formData.location,
        address: formData.address,
        latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
        longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
        full_price: parseFloat(formData.full_price),
        deposit_type: formData.deposit_type as 'fixed' | 'percentage',
        deposit_value: parseFloat(formData.deposit_value),
        capacity: parseInt(formData.capacity),
        bedrooms: parseInt(formData.bedrooms),
        bathrooms: parseInt(formData.bathrooms),
        amenities: formData.amenities,
        check_in_time: formData.check_in_time,
        check_out_time: formData.check_out_time,
        featured: formData.featured,
        active: formData.active,
      };

      console.log('🔧 PropertyForm submitting data:', propertyData);

      // Call the onSave callback which will handle the API call
      onSave(propertyData, mediaFiles);
    } catch (error) {
      console.error("Error saving property:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {property ? t("updateProperty") : t("createProperty")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>{t("propertyName")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t("propertyName")}</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type_id">{t("propertyType")}</Label>
                  <Select value={formData.type_id} onValueChange={(value) => handleInputChange("type_id", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder={t("propertyType")} />
                    </SelectTrigger>
                    <SelectContent>
                      {propertyTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id.toString()}>
                          {type.icon} {type.name_en}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="description_en">{t("descriptionEnglish")}</Label>
                  <Textarea
                    id="description_en"
                    value={formData.description_en}
                    onChange={(e) => handleInputChange("description_en", e.target.value)}
                    placeholder={t("enterDescriptionEnglish")}
                    rows={3}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description_ar">{t("descriptionArabic")}</Label>
                  <Textarea
                    id="description_ar"
                    value={formData.description_ar}
                    onChange={(e) => handleInputChange("description_ar", e.target.value)}
                    placeholder={t("enterDescriptionArabic")}
                    dir="rtl"
                    rows={3}
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Location Information with Google Maps */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="location">{t("location")}</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => handleInputChange("location", e.target.value)}
                placeholder={t("cityOrRegion") || "City or Region"}
                required
              />
            </div>
            
            <MapLocationPicker
              latitude={parseFloat(formData.latitude) || 24.7136}
              longitude={parseFloat(formData.longitude) || 46.6753}
              address={formData.address}
              onLocationChange={handleLocationChange}
            />
          </div>

          {/* Pricing Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                {t("price")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="full_price" className="flex items-center gap-2">
                  {t("fullPrice")}
                  <SaudiRiyal showAmount={false} variant="new" className="text-sm" />
                </Label>
                <Input
                  id="full_price"
                  type="number"
                  value={formData.full_price}
                  onChange={(e) => handleInputChange("full_price", e.target.value)}
                  required
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="deposit_type">{t("depositType")}</Label>
                  <Select value={formData.deposit_type} onValueChange={(value) => handleInputChange("deposit_type", value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">{t("fixed")}</SelectItem>
                      <SelectItem value="percentage">{t("percentage")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="deposit_value" className="flex items-center gap-2">
                    {t("depositValue")}
                    {formData.deposit_type === 'fixed' && (
                      <SaudiRiyal showAmount={false} variant="new" className="text-sm" />
                    )}
                  </Label>
                  <Input
                    id="deposit_value"
                    type="number"
                    value={formData.deposit_value}
                    onChange={(e) => handleInputChange("deposit_value", e.target.value)}
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Capacity Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {t("capacity")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="capacity" className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    {t("capacity")}
                  </Label>
                  <Input
                    id="capacity"
                    type="number"
                    value={formData.capacity}
                    onChange={(e) => handleInputChange("capacity", e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bedrooms" className="flex items-center gap-2">
                    <Bed className="h-4 w-4" />
                    {t("bedrooms")}
                  </Label>
                  <Input
                    id="bedrooms"
                    type="number"
                    value={formData.bedrooms}
                    onChange={(e) => handleInputChange("bedrooms", e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bathrooms" className="flex items-center gap-2">
                    <Bath className="h-4 w-4" />
                    {t("bathrooms")}
                  </Label>
                  <Input
                    id="bathrooms"
                    type="number"
                    value={formData.bathrooms}
                    onChange={(e) => handleInputChange("bathrooms", e.target.value)}
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Amenities */}
          <Card>
            <CardHeader>
              <CardTitle>{t("amenities")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {availableAmenities.map((amenity) => (
                  <div key={amenity.key} className="flex items-center space-x-2">
                    <Checkbox
                      id={amenity.key}
                      checked={formData.amenities.includes(amenity.key)}
                      onCheckedChange={() => handleAmenityToggle(amenity.key)}
                    />
                    <Label htmlFor={amenity.key} className="text-sm">
                      {amenity.name}
                    </Label>
                  </div>
                ))}
              </div>
              {formData.amenities.length > 0 && (
                <div className="mt-4">
                  <div className="flex flex-wrap gap-2">
                    {formData.amenities.map((amenity) => (
                      <Badge key={amenity} variant="secondary" className="flex items-center gap-1">
                        {availableAmenities.find(a => a.key === amenity)?.name}
                        <X 
                          className="h-3 w-3 cursor-pointer" 
                          onClick={() => handleAmenityToggle(amenity)}
                        />
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Check-in/Check-out Times */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                {t("checkInTime")} / {t("checkOutTime")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="check_in_time">{t("checkInTime")}</Label>
                  <Input
                    id="check_in_time"
                    type="time"
                    value={formData.check_in_time.substring(0, 5)} // Display as HH:MM
                    onChange={(e) => handleInputChange("check_in_time", e.target.value + ":00")} // Store as HH:MM:SS
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="check_out_time">{t("checkOutTime")}</Label>
                  <Input
                    id="check_out_time"
                    type="time"
                    value={formData.check_out_time.substring(0, 5)} // Display as HH:MM
                    onChange={(e) => handleInputChange("check_out_time", e.target.value + ":00")} // Store as HH:MM:SS
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Status Options */}
          <Card>
            <CardHeader>
              <CardTitle>{t("status")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="featured"
                    checked={formData.featured}
                    onCheckedChange={(checked) => handleInputChange("featured", checked)}
                  />
                  <Label htmlFor="featured">{t("featured")}</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="active"
                    checked={formData.active}
                    onCheckedChange={(checked) => handleInputChange("active", checked)}
                  />
                  <Label htmlFor="active">{t("active")}</Label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Media Upload */}
          <MediaUpload
            media={media}
            onMediaChange={setMedia}
            onFilesChange={setMediaFiles}
            propertyId={property?.id?.toString()}
          />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t("cancel")}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? t("loading") : (property ? t("save") : t("createProperty"))}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
